import { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import axios from 'axios';
import { Dialog, DialogClose, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogOverlay } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import Select from 'react-select';
import { useDispatch, useSelector } from 'react-redux';
import { SET_DIALOG_OPEN, SET_SONG_TO_ADD } from '@/redux/features/music/leaderboardSlice';
import { SET_USERSONGS } from '@/redux/features/music/musicSlice';

const AddSongToLeaderboard = () => {

    const dispatch = useDispatch();
    const [categories, setCategories] = useState([]);
    const [subCategories, setSubCategories] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [selectedSubCategory, setSelectedSubCategory] = useState(null);
    const { songToAdd, dialogOpen } = useSelector(state => state.leaderboard);
    const [songInfo, setSongInfo] = useState(null)
    const [loading, setLoading] = useState(false)
    const { userSongs } = useSelector(state => state.music);

    const [isDarkMode, setIsDarkMode] = useState(
        document.documentElement.classList.contains("dark")
    );

    useEffect(() => {
        const observer = new MutationObserver(() => {
            setIsDarkMode(document.documentElement.classList.contains("dark"));
        });

        observer.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });

        return () => observer.disconnect();
    }, []);

    // Fetch categories on component mount
    useEffect(() => {
        const fetchCategories = async () => {
            try {
                const { data } = await axios.get('/categories');
                // Transform the data for react-select format
                const formattedCategories = data.map(category => ({
                    value: category.category_id,
                    label: category.name,
                    subcategories: category.subcategories
                }));
                setCategories(formattedCategories);
            } catch (error) {
                toast.error('Failed to load categories');
            }
        };
        fetchCategories();
    }, []);

    // Update subcategories when category changes
    useEffect(() => {

        if (selectedCategory) {
            const category = categories.find(cat => cat.value === selectedCategory.value);
            const formattedSubcategories = category.subcategories.map(sub => ({
                value: sub.subcategory_id,
                label: sub.name
            }));
            setSubCategories(formattedSubcategories);
            setSelectedSubCategory(null); // Reset subcategory selection
        } else {
            setSubCategories([]);
            setSelectedSubCategory(null);
        }
    }, [selectedCategory, categories]);

    useEffect(() => {
        async function checkSongRank() {
            const { data } = await axios.get(`/song/rank?song_id=${songToAdd?.song_id}`)
            setSongInfo(data)
        }
        if (songToAdd?.is_competing) {
            checkSongRank()
        }
    }, [songToAdd])

    const addSongToLeaderboard = async () => {
        if (!selectedCategory) {
            toast.error('Please select a category and sub-category');
            return;
        }
        if (!selectedSubCategory) {
            toast.error('Please select a sub-category');
            return;
        }
        try {
            const token = localStorage.getItem('token')
            setLoading(true)
            await axios.post("/category/add", { song_id: songToAdd?.song_id, subcategory_id: selectedSubCategory?.value }, { headers: { Authorization: `Bearer ${token}` } })
            // Update the song's is_competing status in Redux
            const updatedSongs = userSongs.map(song =>
                song.song_id === songToAdd.song_id
                    ? { ...song, is_competing: true }
                    : song
            );
            dispatch(SET_USERSONGS(updatedSongs));
            // remove pointer-events from body
            setTimeout(() => {
                document.body.style.removeProperty('pointer-events');
            }, 500);
            toast.success("Song added to leaderboard")
            dispatch(SET_DIALOG_OPEN(false))
            dispatch(SET_SONG_TO_ADD(null))
            setSelectedCategory(null)
            setSelectedSubCategory(null)
            setLoading(false)
        } catch (error) {
            setLoading(false)
            console.log(error);
        }
    }

    const onOpenChange = (value) => {
        dispatch(SET_DIALOG_OPEN(value));
        dispatch(SET_SONG_TO_ADD(null))
        setSelectedCategory(null)
        setSelectedSubCategory(null)
        if (value === false) {
            setTimeout(() => {
                document.body.style.removeProperty('pointer-events');
            }, 500);
        }
    }

    const removeSongFromLeaderboard = async () => {
        const token = localStorage.getItem('token')
        try {
            setLoading(true)
            await axios.post(`/category/remove`, { song_id: songToAdd?.song_id }, { headers: { Authorization: `Bearer ${token}` } })
            // Update the song's is_competing status in Redux
            const updatedSongs = userSongs.map(song =>
                song.song_id === songToAdd.song_id
                    ? { ...song, is_competing: false }
                    : song
            );
            dispatch(SET_USERSONGS(updatedSongs));

            toast.success("Song removed from leaderboard")
            dispatch(SET_DIALOG_OPEN(false))
            dispatch(SET_SONG_TO_ADD(null))
            setSelectedCategory(null)
            setSelectedSubCategory(null)
            onOpenChange(false)
            setLoading(false)
        } catch (error) {
            setLoading(false)
            console.log(error)
        }
    }

    // Custom styles for react-select
    const customStyles = {
        control: (base) => ({
            ...base,
            background: isDarkMode ? "rgb(249 168 212)" : "rgb(30 41 59)", // pink-300 or slate-800
            borderColor: isDarkMode ? "rgb(190 24 93)" : "rgb(51 65 85)", // pink-700 or slate-700
            boxShadow: "none",
            "&:hover": {
                borderColor: isDarkMode ? "rgb(190 24 93)" : "rgb(51 65 85)", // pink-700 or slate-700
            },
        }),
        menu: (base) => ({
            ...base,
            background: isDarkMode ? "rgb(249 168 212)" : "rgb(30 41 59)", // pink-300 or slate-800
            border: isDarkMode ? "1px solid rgb(190 24 93)" : "none", // pink-700 border in dark mode
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            height: "190px",
        }),
        option: (base, state) => ({
            ...base,
            background: isDarkMode
                ? state.isSelected
                    ? "rgb(190 24 93)" // pink-700
                    : state.isFocused
                        ? "rgb(236 72 153)" // pink-500
                        : "rgb(249 168 212)" // pink-300
                : state.isSelected
                    ? "rgb(74 222 128)" // green-400
                    : state.isFocused
                        ? "rgb(51 65 85)" // slate-700
                        : "rgb(30 41 59)", // slate-800
            color: isDarkMode
                ? state.isSelected
                    ? "rgb(255 255 255)" // white
                    : "rgb(31 41 55)" // gray-800
                : state.isSelected
                    ? "rgb(0 0 0)" // black
                    : "rgb(226 232 240)", // slate-200
            cursor: "pointer",
            "&:active": {
                background: isDarkMode
                    ? "rgb(190 24 93)" // pink-700
                    : "rgb(74 222 128)", // green-400
            }
        }),
        singleValue: (base) => ({
            ...base,
            color: isDarkMode ? "rgb(31 41 55)" : "rgb(226 232 240)", // gray-800 or slate-200
        }),
        input: (base) => ({
            ...base,
            color: isDarkMode ? "rgb(31 41 55)" : "rgb(226 232 240)", // gray-800 or slate-200
        }),
        placeholder: (base) => ({
            ...base,
            color: isDarkMode ? "rgb(31 41 55)" : "rgb(148 163 184)", // gray-800 or slate-400
        }),
        dropdownIndicator: (base) => ({
            ...base,
            color: isDarkMode ? "rgb(31 41 55)" : "rgb(148 163 184)", // gray-800 or slate-400
            "&:hover": {
                color: isDarkMode ? "rgb(190 24 93)" : "rgb(226 232 240)", // pink-700 or slate-200
            },
        }),
        clearIndicator: (base) => ({
            ...base,
            color: isDarkMode ? "rgb(31 41 55)" : "rgb(148 163 184)", // gray-800 or slate-400
            "&:hover": {
                color: isDarkMode ? "rgb(190 24 93)" : "rgb(226 232 240)", // pink-700 or slate-200
            },
        }),
    };

    return (
        <Dialog open={dialogOpen} onOpenChange={onOpenChange} className="p-4" >
            <DialogOverlay className="z-[999]" >
                <DialogContent className=" z-[9999] w-[95%] sm:w-full max-w-md sm:p-4 p-2 rounded-lg bg-gradient-to-r from-slate-950 via-slate-900 to-slate-950 dark:from-pink-200 dark:via-pink-300 dark:to-pink-200 text-slate-200 dark:text-slate-800 border border-green-400/50 dark:border-pink-700/50 mx-auto">
                    <DialogHeader className="space-y-2">
                        <div className='flex flex-col items-start'>
                            <DialogTitle className="text-xl font-semibold text-slate-100 dark:text-slate-800 font-potta">
                                {songToAdd?.is_competing ? "Song is in leaderboard" : "Add Song to Leaderboard"}
                            </DialogTitle>
                            <DialogDescription className="text-sm text-green-400 dark:text-pink-700 font-poppins font-medium mt-1 text-left">
                                {songToAdd?.is_competing ? (<>
                                    <span>Competing In: {songInfo?.category?.name} &gt; {songInfo?.subcategory?.name}</span>
                                    <span className='block text-left' >Rank: {songInfo?.rankings?.subcategory?.rank}</span>
                                </>) : (
                                    <>Confirm adding song to leaderboard
                                        {!songToAdd?.publish && <span className="text-red-500 block">Private songs will be visible on the leaderboard.</span>}
                                    </>
                                )}
                            </DialogDescription>
                        </div>
                    </DialogHeader>

                    <div className="relative min-w-0 flex items-center sm:gap-4 gap-2 bg-slate-800 dark:bg-pink-200 sm:p-2 p-1 rounded-lg ">
                        <img
                            src={songToAdd?.image_url}
                            alt="Song Cover"
                            className="sm:w-12 sm:h-12 w-10 h-10 rounded-lg flex-shrink-0"
                        />
                        <div className="flex flex-col items-start w-full truncate">
                            <p className="font-semibold text-slate-100 dark:text-slate-800 truncate w-full">{songToAdd?.song_name}</p>
                        </div>
                    </div>

                    {!songToAdd?.is_competing && <div className="space-y-2.5 mt-2">
                        <Select
                            placeholder="Select Category"
                            options={categories}
                            value={selectedCategory}
                            onChange={setSelectedCategory}
                            styles={customStyles}
                            className="text-slate-200 dark:text-slate-800"
                            isClearable
                        />
                        <Select
                            placeholder="Select Sub-Category"
                            options={subCategories}
                            value={selectedSubCategory}
                            onChange={setSelectedSubCategory}
                            styles={customStyles}
                            className="text-slate-200 dark:text-slate-800"
                            isDisabled={!selectedCategory}
                            isClearable
                        />
                    </div>}

                    <DialogFooter className="mt-2">
                        <div className="flex w-full justify-center gap-2 ">
                            {!songToAdd?.is_competing ? <Button onClick={addSongToLeaderboard} disabled={loading} className="w-full disabled:animate-pulse  bg-green-400 dark:bg-gradient-to-br dark:from-pink-600 dark:to-pink-900 hover:bg-green-300 text-slate-900">{!loading ? "Add" : "Adding..."}</Button> :
                                <Button onClick={() => {
                                    removeSongFromLeaderboard().then(() => {
                                        // Ensure pointer-events are restored after the async operation completes
                                        document.body.style.removeProperty('pointer-events');
                                    }).catch(() => {
                                        // Also ensure pointer-events are restored in case of an error
                                        document.body.style.removeProperty('pointer-events');
                                    });
                                }} disabled={loading} className="w-full disabled:animate-pulse bg-red-500 hover:bg-red-400 text-slate-900">
                                    {!loading ? "Remove" : "Removing..."}
                                </Button>}
                            <DialogClose asChild>
                                <Button onClick={() => {
                                    onOpenChange(false);
                                    // Restore pointer-events immediately upon cancellation
                                    document.body.style.removeProperty('pointer-events');
                                }} variant="secondary" className="w-full bg-slate-700 dark:bg-pink-400 hover:bg-slate-800 dark:hover:bg-pink-500 text-slate-300 dark:text-slate-800">
                                    Cancel
                                </Button>
                            </DialogClose>
                        </div>
                    </DialogFooter>
                </DialogContent>
            </DialogOverlay>
        </Dialog>
    );
};

export default AddSongToLeaderboard;